// src/App.tsx
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { LoginPage } from './auth/LoginPage'
import { RequireAuth } from './auth/RequireAuth'
import { Shell } from './shell/Shell'
import { CockpitPage } from './cockpit/CockpitPage'
import { SettingsPage } from './settings/SettingsPage'
import { CrmLayout } from './crm/CrmLayout'
import { CrmDashboardPage } from './crm/CrmDashboardPage'
import { PipelinePage } from './crm/pipeline/PipelinePage'
import { CampaignsPage } from './crm/campaigns/CampaignsPage'
import { CampaignDetailPage } from './crm/campaigns/CampaignDetailPage'
import { TemplatesPage } from './crm/templates/TemplatesPage'
import { CrmCreatorsPage } from './crm/CrmCreatorsPage'
import { CreatorDetailPage } from './crm/CreatorDetailPage'
import { CreatorFormPage } from './crm/CreatorFormPage'
import { ApplyPage } from './apply/ApplyPage'
import { ApplicationsPage } from './crm/applications/ApplicationsPage'
import { UnmatchedEmailsPage } from './crm/UnmatchedEmailsPage'
import { TasksPage } from './crm/tasks/TasksPage'
import { UgcPage } from './crm/ugc/UgcPage'
import { ApplicationsTab } from './crm/ugc/ApplicationsTab'
import { VideosTab } from './crm/ugc/VideosTab'
import { PaymentsTab } from './crm/ugc/PaymentsTab'
import { ProTab } from './crm/ugc/ProTab'
import { RatesTab } from './crm/ugc/RatesTab'
import { MessagesTab } from './crm/ugc/MessagesTab'
import { ReferralsTab } from './crm/ugc/ReferralsTab'
import { StatsPage } from './stats/StatsPage'
import { lazy, Suspense } from 'react'
import { MarketingLayout } from './marketing/MarketingLayout'
import { MarketingDashboardPage } from './marketing/MarketingDashboardPage'
import { LaunchPage } from './marketing/launch/LaunchPage'
import { DocsPage } from './marketing/docs/DocsPage'
import { ChannelsBudgetPage } from './marketing/channels/ChannelsBudgetPage'
import { ContentPage } from './marketing/content/ContentPage'
import { ContentDetailPage } from './marketing/content/ContentDetailPage'
import { FinanceLayout } from './finance/FinanceLayout'
import { CalculatorPage } from './finance/CalculatorPage'
import { TrackingPage } from './finance/TrackingPage'

const AsoPage = lazy(() =>
  import('./marketing/aso/AsoPage').then((m) => ({ default: m.AsoPage })),
)

const PromptsPage = lazy(() =>
  import('./marketing/prompts/PromptsPage').then((m) => ({ default: m.PromptsPage })),
)

const MesurePage = lazy(() =>
  import('./marketing/mesure/MesurePage').then((m) => ({ default: m.MesurePage })),
)

const ReportPage = lazy(() =>
  import('./reports/ReportPage').then((m) => ({ default: m.ReportPage })),
)

function ReportRoute() {
  return (
    <Suspense
      fallback={
        <div style={{ padding: '2rem', color: 'var(--muted)', fontSize: '0.875rem' }}>
          Chargement du rapport…
        </div>
      }
    >
      <ReportPage />
    </Suspense>
  )
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
})

/** Redirige /crm/creators/:id[/edit] vers son équivalent /crm/contacts (client-side). */
function LegacyCreatorRedirect({ suffix = '' }: { suffix?: string }) {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/crm/contacts/${id ?? ''}${suffix}`} replace />
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Routes publiques */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/apply/:slug" element={<ApplyPage />} />
          {/* Routes protégées */}
          <Route
            element={
              <RequireAuth>
                <Shell />
              </RequireAuth>
            }
          >
            <Route index element={<CockpitPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="stats" element={<StatsPage />} />
            {/* Rapports (rapport quotidien) */}
            <Route path="reports" element={<ReportRoute />} />
            <Route path="reports/:date" element={<ReportRoute />} />
            {/* CRM */}
            <Route path="crm" element={<CrmLayout />}>
              <Route index element={<CrmDashboardPage />} />
              <Route path="pipeline" element={<PipelinePage />} />
              <Route path="contacts" element={<CrmCreatorsPage />} />
              <Route path="contacts/new" element={<CreatorFormPage />} />
              <Route path="contacts/:id" element={<CreatorDetailPage />} />
              <Route path="contacts/:id/edit" element={<CreatorFormPage />} />
              {/* Redirections legacy (client-side, pas un 301 HTTP) */}
              <Route path="creators" element={<Navigate to="/crm/contacts" replace />} />
              <Route path="creators/new" element={<Navigate to="/crm/contacts/new" replace />} />
              <Route path="creators/:id" element={<LegacyCreatorRedirect />} />
              <Route path="creators/:id/edit" element={<LegacyCreatorRedirect suffix="/edit" />} />
              <Route path="campaigns" element={<CampaignsPage />} />
              <Route path="campaigns/:id" element={<CampaignDetailPage />} />
              <Route path="templates" element={<TemplatesPage />} />
              <Route path="applications" element={<ApplicationsPage />} />
              <Route path="unmatched" element={<UnmatchedEmailsPage />} />
              <Route path="tasks" element={<TasksPage />} />
              {/* Programme UGC */}
              <Route path="ugc" element={<UgcPage />}>
                <Route index element={<Navigate to="candidatures" replace />} />
                <Route path="candidatures" element={<ApplicationsTab />} />
                <Route path="videos" element={<VideosTab />} />
                <Route path="paiements" element={<PaymentsTab />} />
                <Route path="parrainages" element={<ReferralsTab />} />
                <Route path="pro" element={<ProTab />} />
                <Route path="montants" element={<RatesTab />} />
                <Route path="messages" element={<MessagesTab />} />
              </Route>
            </Route>
            {/* Marketing */}
            <Route path="marketing" element={<MarketingLayout />}>
              <Route index element={<MarketingDashboardPage />} />
              <Route path="launch" element={<LaunchPage />} />
              <Route path="docs" element={<DocsPage />} />
              <Route
                path="aso"
                element={
                  <Suspense
                    fallback={
                      <div style={{ padding: '2rem', color: 'var(--muted)', fontSize: '0.875rem' }}>
                        Chargement ASO…
                      </div>
                    }
                  >
                    <AsoPage />
                  </Suspense>
                }
              />
              <Route path="content" element={<ContentPage />} />
              <Route path="content/new" element={<ContentDetailPage />} />
              <Route path="content/:id" element={<ContentDetailPage />} />
              <Route
                path="prompts"
                element={
                  <Suspense
                    fallback={
                      <div style={{ padding: '2rem', color: 'var(--muted)', fontSize: '0.875rem' }}>
                        Chargement des prompts…
                      </div>
                    }
                  >
                    <PromptsPage />
                  </Suspense>
                }
              />
              <Route path="channels" element={<ChannelsBudgetPage />} />
              <Route
                path="mesure"
                element={
                  <Suspense
                    fallback={
                      <div style={{ padding: '2rem', color: 'var(--muted)', fontSize: '0.875rem' }}>
                        Chargement du tunnel…
                      </div>
                    }
                  >
                    <MesurePage />
                  </Suspense>
                }
              />
            </Route>
            {/* Finance */}
            <Route path="finance" element={<FinanceLayout />}>
              <Route index element={<CalculatorPage />} />
              <Route path="suivi" element={<TrackingPage />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
